import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import React, { FC } from 'react';
import { useViewClaimDocument } from '../../hooks/useViewClaimDocument';
import { ClaimMetadata } from '../../redux/types';
import { formatDateIso } from '../../utils/dateUtils';
import { useTranslation } from '../blocks';
import { MultiColumnInfoBoxTile } from '../blocks/MultiColumnInfoBoxTile';
import { ClaimReferenceCell } from './ClaimReferenceCell';
import { ClaimStatusIcon } from './ClaimStatusIcon';
import LabeledField from './LabeledField';

type ClaimSummaryProps = {
    claimData: ClaimMetadata;
};

export const ClaimSummary: FC<ClaimSummaryProps> = ({ claimData }) => {
    const { t } = useTranslation('claimsListPage');
    const { t: tClaimDetails } = useTranslation('claimDetails');
    const downloadDocument = useViewClaimDocument();

    const handleViewClaimDocument = async (uploadId: string) => {
        await downloadDocument(claimData.resellerId, uploadId);
    };

    return (
        <>
            <Column width={'100%'} spacing={'medium'}>
                {claimData.errorList?.length > 0 && (
                    <Alert size={'large'} type={'error'}>
                        <Text type={'b300'}>{t('failedToCreateClaim-text')}</Text>
                    </Alert>
                )}
                <MultiColumnInfoBoxTile
                    widths={['grid-3', 'grid-3', 'grid-3', 'grid-3']}
                    title={t('claimSummary-header')}
                >
                    <>
                        <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                            <Text type={'h200'}>{t('claimNumber-columnTitle')}</Text>
                            <Text type={'b300'} tag={'p'}>
                                {t.cognate(claimData.resellerClaimNumber)}
                            </Text>
                        </Column>
                        <LabeledField
                            label={t('createdByUser-columnTitle')}
                            text={t.cognate(claimData.createdByUser)}
                        />
                    </>
                    <>
                        <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                            <Text type={'h200'}>{t('status-columnTitle')}</Text>
                            <ClaimStatusIcon status={claimData.status} errors={claimData.errorList} />
                        </Column>
                        <LabeledField
                            label={t('creationDate-columnTitle')}
                            text={t.cognate(formatDateIso(claimData.submissionDate))}
                        />
                    </>
                    <>
                        <LabeledField
                            label={t('creditMemoStatus-columnTitle')}
                            text={t('creditMemo', { context: claimData.creditMemoStatus })}
                        />
                        <LabeledField
                            label={t('creditMemoNumber-columnTitle')}
                            text={t.cognate(claimData.creditMemoNumber || t('noCreditMemoNumber-text'))}
                        />
                    </>
                    <>
                        {claimData.fileId && (
                            <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                                <Text type={'h200'}>{tClaimDetails('claimFile-field')}</Text>
                                <ClaimReferenceCell
                                    translatedText={tClaimDetails('downloadUploadedClaimFile-text')}
                                    fileId={claimData.fileId}
                                    callback={handleViewClaimDocument}
                                />
                            </Column>
                        )}
                    </>
                </MultiColumnInfoBoxTile>
            </Column>
        </>
    );
};
